/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'preact/compat';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { events } from '@adobe-commerce/event-bus';
import { Price, InLineAlert } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { CheckWithCircle, WarningFilled } from '@adobe-commerce/elsie/icons';
import { NegotiableQuoteTemplateModel } from '@/quote-management/data/models/negotiable-quote-template-model';
import { CartItemModel } from '@/quote-management/data/models/negotiable-quote-model';
import {
  QuotePricesSummary,
  ProductListTable,
  ItemsQuoted as ItemsQuotedComponent,
  ConfirmationModal,
} from '@/quote-management/components';
import { LineItemNoteModal } from '@/quote-management/components/LineItemNoteModal';
import { addQuoteTemplateLineItemNote } from '@/quote-management/api';
import { useRemoveTemplateItems } from '@/quote-management/hooks/useRemoveTemplateItems';
import { useItemsQuotedTemplate } from '@/quote-management/hooks/useItemsQuotedTemplate';
import { useUpdateTemplateQuantities } from '@/quote-management/hooks/useUpdateTemplateQuantities';

export interface ItemsQuotedTemplateProps
  extends HTMLAttributes<HTMLDivElement> {
  templateData?: NegotiableQuoteTemplateModel;
  slots?: {
    ProductListTable?: SlotProps<{
      items: NegotiableQuoteTemplateModel['items'];
      canEdit: boolean;
      dropdownSelections: Record<string, string | undefined>;
      handleItemDropdownChange: (item: CartItemModel, action: string) => void;
      handleQuantityChange: (item: CartItemModel, newQuantity: number) => void;
      handleUpdate: (e: SubmitEvent) => void;
      onItemDropdownChange?: (item: any, action: string) => void;
    }>;
    QuotePricesSummary?: SlotProps<{
      items: NegotiableQuoteTemplateModel['items'];
      prices: NegotiableQuoteTemplateModel['prices'];
    }>;
  };
}

export const ItemsQuotedTemplate: Container<ItemsQuotedTemplateProps> = ({
  templateData: initialData,
  slots,
  ...props
}) => {
  const [templateData, setTemplateData] = useState<
    NegotiableQuoteTemplateModel | undefined
  >(initialData);
  const [selectedItem, setSelectedItem] = useState<CartItemModel | null>(null);
  const [isLineItemNoteModalOpen, setIsLineItemNoteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState('');
  const [dropdownSelections, setDropdownSelections] = useState<
    Record<string, string>
  >({});

  const dictionary = useText({
    subtotal:
      'NegotiableQuoteTemplate.Manage.quotePricesSummary.subtotal.excludingTax',
    grandTotal:
      'NegotiableQuoteTemplate.Manage.quotePricesSummary.grandTotal.includingTax',
    appliedTaxes:
      'NegotiableQuoteTemplate.Manage.quotePricesSummary.appliedTaxes',
    removeModalTitle: 'NegotiableQuoteTemplate.Manage.removeItemsModal.title',
    removeModalDescription:
      'NegotiableQuoteTemplate.Manage.removeItemsModal.description',
    removeModalCancelButton:
      'NegotiableQuoteTemplate.Manage.removeItemsModal.cancelButton',
    removeModalConfirmButton:
      'NegotiableQuoteTemplate.Manage.removeItemsModal.confirmButton',
    removeModalConfirmButtonRemoving:
      'NegotiableQuoteTemplate.Manage.removeItemsModal.confirmButtonRemoving',
    removeSuccessHeading:
      'NegotiableQuoteTemplate.Manage.removeItemsModal.successHeading',
    removeSuccessMessage:
      'NegotiableQuoteTemplate.Manage.removeItemsModal.successMessage',
    removeErrorHeading:
      'NegotiableQuoteTemplate.Manage.removeItemsModal.errorHeading',
    removeErrorMessage:
      'NegotiableQuoteTemplate.Manage.removeItemsModal.errorMessage',
    updateModalTitle:
      'NegotiableQuoteTemplate.Manage.updateQuantitiesModal.title',
    updateModalDescription:
      'NegotiableQuoteTemplate.Manage.updateQuantitiesModal.description',
    updateModalCancelButton:
      'NegotiableQuoteTemplate.Manage.updateQuantitiesModal.cancelButton',
    updateModalUpdateButton:
      'NegotiableQuoteTemplate.Manage.updateQuantitiesModal.updateButton',
    updateSuccessHeading:
      'NegotiableQuoteTemplate.Manage.updateQuantitiesModal.successHeading',
    updateSuccessMessage:
      'NegotiableQuoteTemplate.Manage.updateQuantitiesModal.successMessage',
    updateErrorHeading:
      'NegotiableQuoteTemplate.Manage.updateQuantitiesModal.errorHeading',
    updateErrorMessage:
      'NegotiableQuoteTemplate.Manage.updateQuantitiesModal.errorMessage',
    errorHeading:
      'NegotiableQuoteTemplate.Manage.lineItemNoteModal.errorHeading',
  });

  // Use the remove template items hook
  const {
    handleRemoveItems,
    handleConfirmRemove,
    handleCancelRemove,
    isRemoveModalOpen,
    isRemoving,
    removeNotificationState,
    itemsToRemove,
  } = useRemoveTemplateItems({
    templateId: templateData?.id,
    removeSuccessMessage: dictionary.removeSuccessMessage,
    removeErrorMessage: dictionary.removeErrorMessage,
  });

  // Use the items quoted template hook for dropdown and interaction logic
  const {
    dropdownSelections: hookDropdownSelections,
    handleItemDropdownChange: hookHandleItemDropdownChange,
    handleDismissRemoveBanner,
    clearDropdownSelection,
  } = useItemsQuotedTemplate({
    handleRemoveItems,
  });

  // Use the update template quantities hook
  const {
    handleQuantityChange,
    handleUpdate,
    handleConfirmUpdate,
    handleCancelUpdate,
    handleDismissBanner,
    isUpdateModalOpen,
    updateNotificationState,
  } = useUpdateTemplateQuantities({
    templateId: templateData?.id,
    updateSuccessMessage: dictionary.updateSuccessMessage,
    updateErrorMessage: dictionary.updateErrorMessage,
  });

  useEffect(() => {
    const templateDataEvent = events.on(
      'quote-management/quote-template-data',
      (payload) => {
        setTemplateData(payload.quoteTemplate);
        setDropdownSelections({});
      },
      { eager: true }
    );

    return () => templateDataEvent?.off();
  }, []);

  // Wrapper for cancel remove that also clears dropdown selection
  const handleCancelRemoveWithCleanup = useCallback(() => {
    // Clear dropdown selections for all cancelled items
    itemsToRemove.forEach((item) => {
      clearDropdownSelection(item);
    });
    handleCancelRemove();
  }, [itemsToRemove, clearDropdownSelection, handleCancelRemove]);

  // Combined handler for dropdown selection that handles both remove (via hook) and edit (for line item notes)
  const handleItemDropdownChange = useCallback(
    (item: CartItemModel, action: string) => {
      const templateItem = item;

      // Handle edit action - open line item note modal
      if (action === 'edit') {
        setSelectedItem(templateItem);
        setIsLineItemNoteModalOpen(true);
        // Keep 'edit' selected while modal is open - dropdown is cleared in handleModalClose
        setDropdownSelections((prev) => ({
          ...prev,
          [templateItem.uid]: action,
        }));
        return;
      }

      // For remove and other actions, delegate to hook handler
      hookHandleItemDropdownChange(item, action);
    },
    [hookHandleItemDropdownChange]
  );

  // Merge dropdown selections from hook and local state
  const mergedDropdownSelections = {
    ...hookDropdownSelections,
    ...dropdownSelections,
  };

  // Handle modal close
  const handleModalClose = useCallback(() => {
    // Clean up dropdown selection for the selected item before clearing it
    /* istanbul ignore else: defensive check - selectedItem should always be truthy when modal is open */
    if (selectedItem) {
      setDropdownSelections((prev) => {
        const next = { ...prev };
        delete next[selectedItem.uid];
        return next;
      });
    }
    setIsLineItemNoteModalOpen(false);
    setSelectedItem(null);
    setIsSubmitting(false);
    setModalErrorMessage('');
  }, [selectedItem]);

  // Handle modal confirm - submit note only (quantity is read-only for templates)
  const handleModalConfirm = useCallback(
    async (note: string) => {
      /* istanbul ignore next: defensive guard for async race conditions - unreachable in normal flow as modal only renders with selectedItem and templateData */
      if (!selectedItem || !templateData) return;

      setIsSubmitting(true);
      setModalErrorMessage('');

      try {
        // Set the line item note
        await addQuoteTemplateLineItemNote({
          templateId: templateData.id,
          itemId: selectedItem.uid,
          note,
        });

        // Close modal on success
        handleModalClose();
      } catch (error) {
        console.error('Failed to set template line item note:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unable to update the item. Please try again.';
        setModalErrorMessage(errorMessage);
        setIsSubmitting(false);
      }
    },
    [selectedItem, templateData, handleModalClose]
  );

  // Create remove confirmation banner based on notification state
  const removeConfirmationBanner =
    removeNotificationState.type === 'success' ? (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<CheckWithCircle />}
        heading={dictionary.removeSuccessHeading}
        description={removeNotificationState.message}
        onDismiss={handleDismissRemoveBanner}
        data-testid="remove-items-success-banner"
      />
    ) : removeNotificationState.type === 'error' ? (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<WarningFilled />}
        heading={dictionary.removeErrorHeading}
        description={removeNotificationState.message}
        onDismiss={handleDismissRemoveBanner}
        data-testid="remove-items-error-banner"
      />
    ) : null;

  // Create update quantities confirmation banner based on notification state
  const updateConfirmationBanner =
    updateNotificationState.type === 'success' ? (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<CheckWithCircle />}
        heading={dictionary.updateSuccessHeading}
        description={updateNotificationState.message}
        onDismiss={handleDismissBanner}
        data-testid="update-quantities-success-banner"
      />
    ) : updateNotificationState.type === 'error' ? (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<WarningFilled />}
        heading={dictionary.updateErrorHeading}
        description={updateNotificationState.message}
        onDismiss={handleDismissBanner}
        data-testid="update-quantities-error-banner"
      />
    ) : null;

  if (!templateData) {
    return <ItemsQuotedComponent loading={true} />;
  }

  const removeConfirmationModalProps = isRemoveModalOpen
    ? {
        open: true,
        title: dictionary.removeModalTitle,
        message: dictionary.removeModalDescription,
        cancelLabel: dictionary.removeModalCancelButton,
        confirmLabel: isRemoving
          ? dictionary.removeModalConfirmButtonRemoving
          : dictionary.removeModalConfirmButton,
        onCancel: handleCancelRemoveWithCleanup,
        onConfirm: handleConfirmRemove,
        confirmationBanner: removeConfirmationBanner,
      }
    : null;

  const updateConfirmationModalProps = isUpdateModalOpen
    ? {
        open: true,
        title: dictionary.updateModalTitle,
        message: dictionary.updateModalDescription,
        cancelLabel: dictionary.updateModalCancelButton,
        confirmLabel: dictionary.updateModalUpdateButton,
        onCancel: handleCancelUpdate,
        onConfirm: handleConfirmUpdate,
        confirmationBanner: updateConfirmationBanner,
      }
    : null;

  // Only define a single confirmation modal
  const confirmationModalProps =
    removeConfirmationModalProps || updateConfirmationModalProps;

  const quotePricesSummaryEntries = [];

  templateData.prices.subtotalExcludingTax &&
    quotePricesSummaryEntries.push({
      label: dictionary.subtotal,
      id: 'subtotal',
      value: (
        <Price
          amount={templateData.prices.subtotalExcludingTax.value}
          currency={templateData.prices.subtotalExcludingTax.currency}
          weight="normal"
        />
      ),
    });

  templateData.prices.grandTotal &&
    quotePricesSummaryEntries.push({
      label: dictionary.grandTotal,
      id: 'total',
      value: (
        <Price
          amount={templateData.prices.grandTotal.value}
          currency={templateData.prices.grandTotal.currency}
        />
      ),
      strong: true,
    });

  // Create modal error banner
  const modalErrorBanner = modalErrorMessage ? (
    <InLineAlert
      type="error"
      variant="primary"
      icon={<WarningFilled />}
      heading={dictionary.errorHeading}
      description={modalErrorMessage}
      onDismiss={() => setModalErrorMessage('')}
      data-testid="line-item-note-error-banner"
    />
  ) : null;

  return (
    <>
      <ItemsQuotedComponent
        data-testid="items-quoted-template-container"
        {...props}
        loading={false}
        table={
          <Slot
            name="ProductListTable"
            slot={slots?.ProductListTable}
            context={{
              items: templateData.items,
              canEdit: templateData.canSendForReview,
              dropdownSelections: mergedDropdownSelections,
              handleItemDropdownChange,
              handleQuantityChange,
              handleUpdate,
            }}
          >
            <ProductListTable
              items={templateData.items}
              canEdit={templateData.canSendForReview}
              showActions={templateData.canEditTemplateItems}
              onItemDropdownChange={handleItemDropdownChange}
              onQuantityChange={handleQuantityChange}
              onUpdate={handleUpdate}
              dropdownSelections={mergedDropdownSelections}
            />
          </Slot>
        }
        pricesSummary={
          <Slot
            name="QuotePricesSummary"
            slot={slots?.QuotePricesSummary}
            context={{
              items: templateData.items,
              prices: templateData.prices,
            }}
          >
            <QuotePricesSummary entries={quotePricesSummaryEntries} />
          </Slot>
        }
      />
      {/* Confirmation Modal for remove/update */}
      {confirmationModalProps && (
        <ConfirmationModal {...confirmationModalProps} />
      )}
      {/* Line Item Note Modal */}
      {selectedItem && (
        <LineItemNoteModal
          open={isLineItemNoteModalOpen}
          item={selectedItem as any}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          isSubmitting={isSubmitting}
          readOnlyQuantity={true}
          errorBanner={modalErrorBanner || undefined}
        />
      )}
    </>
  );
};
